
import { useState, useEffect, useRef } from 'react';

export default function ContactSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    message: '',
    kvkk: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.kvkk) {
      alert('KVKK onayını vermeniz gerekmektedir.');
      return;
    }

    if (formData.message.length > 500) {
      alert('Mesaj 500 karakteri geçemez.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('kvkk', formData.kvkk.toString());

      const response = await fetch('https://readdy.ai/api/form/d3oklo4p167v35pquv9g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', message: '', kvkk: false });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      ref={sectionRef}
      id="contact" 
      className="py-20 section-padding"
      style={{
        backgroundImage: `linear-gradient(rgba(11, 11, 14, 0.95), rgba(11, 11, 14, 0.95)), url('https://public.readdy.ai/ai/img_res/66ac67b41b91fc0a72219769b66395d8.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      <div className="container-max">
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'fade-in' : 'opacity-0'}`}>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="gold-text">İletişime</span> Geçin
          </h2>
          <div className="w-24 h-1 gold-gradient mx-auto rounded-full"></div>
          <p className="text-xl text-gray-300 mt-6 max-w-2xl mx-auto">
            Şeffaf plak tedaviniz hakkında sorularınız için bizimle iletişime geçin.
          </p>
        </div>
        
        <div className="max-w-2xl mx-auto">
          <div className={`card-premium transition-all duration-1000 delay-300 ${isVisible ? 'slide-up' : 'opacity-0'}`}>
            <form id="contact_form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-2 gold-text">
                  Ad Soyad *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-black/40 border border-[#D4AF37]/30 rounded-lg focus:border-[#D4AF37] focus:outline-none transition-colors text-white"
                  placeholder="Adınızı ve soyadınızı girin"
                />
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-sm font-medium mb-2 gold-text">
                  Telefon (WhatsApp) *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-black/40 border border-[#D4AF37]/30 rounded-lg focus:border-[#D4AF37] focus:outline-none transition-colors text-white"
                  placeholder="WhatsApp numaranızı girin"
                />
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium mb-2 gold-text">
                  Mesajınız *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 bg-black/40 border border-[#D4AF37]/30 rounded-lg focus:border-[#D4AF37] focus:outline-none transition-colors text-white resize-none"
                  placeholder="Sorularınızı veya mesajınızı yazın (maksimum 500 karakter)"
                />
                <div className="text-right text-sm text-gray-400 mt-1">
                  {formData.message.length}/500
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="kvkk"
                  name="kvkk"
                  checked={formData.kvkk}
                  onChange={handleInputChange}
                  required
                  className="mt-1 w-4 h-4 text-[#D4AF37] bg-black/40 border-[#D4AF37]/30 rounded focus:ring-[#D4AF37] focus:ring-2"
                />
                <label htmlFor="kvkk" className="text-sm text-gray-300">
                  KVKK kapsamında kişisel verilerimin işlenmesini kabul ediyorum. *
                </label>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    Hemen Danışın
                  </>
                )}
              </button>
              
              {submitStatus === 'success' && (
                <div className="text-center p-4 bg-green-500/20 border border-green-500/30 rounded-lg">
                  <i className="ri-check-circle-line text-green-400 text-xl mr-2"></i>
                  <span className="text-green-400">Mesajınız başarıyla gönderildi!</span>
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="text-center p-4 bg-red-500/20 border border-red-500/30 rounded-lg">
                  <i className="ri-error-warning-line text-red-400 text-xl mr-2"></i>
                  <span className="text-red-400">Bir hata oluştu. Lütfen tekrar deneyin.</span>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}